import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AssignRankBodyDto,
  AssignRankResponseDto,
  ExportJournalResponseDto,
  FreezePanelResponseDto,
  RankAdminClient,
  RankFreezeVersionRow,
  RankWorkingTableDto,
  RecordExportBodyDto,
  RecordExportResponseDto,
  RemoveRankBodyDto,
  RemoveRankResponseDto,
} from '@/api/rank-admin-client';
import { useRankAdminClient } from '@/api/rank-admin-client-context';

// =============================================================================
// use-rank-admin.ts — Hooks TanStack Query du mode Rang ADMIN (S17-bis / D2).
// =============================================================================
// Lecture (tableau de travail) en useQuery ; commandes (poser / retirer / figer) en useMutation
// (effets de bord serveur). Le client effectif vient du RankAdminClientProvider (doublable, stub-first).
//
// ZÉRO règle métier ici : l'autorité (RBAC AdminSessionGuard, composition ACL, upsert/idempotence du tag,
// relais du figement) est l'API. Après une mutation de rang, on INVALIDE la query du tableau pour refléter
// l'état serveur (le rang dénormalisé sur les niveaux est recalculé côté API). Le FIGEMENT n'invalide PAS
// le tableau (la vue reste ÉDITABLE, re-figement autorisé — pas de verrouillage). L'erreur API
// (RankAdminApiError) remonte telle quelle.

const RANK_ADMIN_KEY = ['rank-admin'] as const;

/** Clé de query du tableau de travail Rang d'une campagne. */
export function rankWorkingTableKey(campaignId: string): readonly unknown[] {
  return [...RANK_ADMIN_KEY, 'working-table', campaignId];
}

/** Tableau de travail Rang (read-model NOMINATIF au grain trinôme, SANS K-anon). */
export function useRankWorkingTable(
  campaignId: string,
): UseQueryResult<RankWorkingTableDto, Error> {
  const client: RankAdminClient = useRankAdminClient();
  return useQuery<RankWorkingTableDto, Error>({
    queryKey: rankWorkingTableKey(campaignId),
    queryFn: () => client.getRankWorkingTable(campaignId),
  });
}

/** POSER un rang {A,B,PROSPECT} sur le couple fournisseur×profil (upsert). Invalide le tableau. */
export function useAssignRank(
  campaignId: string,
): UseMutationResult<AssignRankResponseDto, Error, AssignRankBodyDto> {
  const client: RankAdminClient = useRankAdminClient();
  const qc = useQueryClient();
  return useMutation<AssignRankResponseDto, Error, AssignRankBodyDto>({
    mutationFn: (body) => client.assignRank(campaignId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rankWorkingTableKey(campaignId) });
    },
  });
}

/** RETIRER un rang (UPDATE → NonReferenced ; idempotent). Invalide le tableau. */
export function useRemoveRank(
  campaignId: string,
): UseMutationResult<RemoveRankResponseDto, Error, RemoveRankBodyDto> {
  const client: RankAdminClient = useRankAdminClient();
  const qc = useQueryClient();
  return useMutation<RemoveRankResponseDto, Error, RemoveRankBodyDto>({
    mutationFn: (body) => client.removeRank(campaignId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: rankWorkingTableKey(campaignId) });
    },
  });
}

/**
 * RELAYER une demande de « Figer le panel » au worker (D3). N'invalide PAS le tableau : la vue reste
 * ÉDITABLE (re-figement autorisé, pas de verrouillage — G0-D1-1, Option A). Le feedback async
 * « demande transmise » est dérivé de `mutation.isSuccess` / `mutation.data.status === 'relayed'`.
 */
export function useFreezePanel(
  campaignId: string,
): UseMutationResult<FreezePanelResponseDto, Error, void> {
  const client: RankAdminClient = useRankAdminClient();
  return useMutation<FreezePanelResponseDto, Error, void>({
    mutationFn: () => client.freezePanel(campaignId),
  });
}

/** Clé de query de la LISTE des versions figées d'une campagne (V26-DELIVERABLE-EXPOSE / TRANCHE A). */
export function rankFreezeListKey(campaignId: string): readonly unknown[] {
  return [...RANK_ADMIN_KEY, 'freezes', campaignId];
}

/**
 * LISTE des versions figées du livrable (métadonnées + présence `hasXlsx`, TRANCHE A ; aucun PDF — A3).
 * Read-only : le figement RÉEL est produit ASYNCHRONE par le worker (D3) — cette query n'est PAS invalidée
 * par `useFreezePanel` (le relais D1 ne crée aucune version immédiatement, cf. commentaire ci-dessus).
 */
export function useListFreezes(
  campaignId: string,
): UseQueryResult<readonly RankFreezeVersionRow[], Error> {
  const client: RankAdminClient = useRankAdminClient();
  return useQuery<readonly RankFreezeVersionRow[], Error>({
    queryKey: rankFreezeListKey(campaignId),
    queryFn: () => client.listFreezes(campaignId),
  });
}

/** Clé de query du JOURNAL UNIFIÉ des exports + figements d'une campagne (V26-EXPORT-JOURNAL). */
export function exportJournalKey(campaignId: string): readonly unknown[] {
  return [...RANK_ADMIN_KEY, 'export-journal', campaignId];
}

/**
 * JOURNAL UNIFIÉ des événements export + figement (déjà trié occurredAt DESC par l'API). Read-only : il
 * est INVALIDÉ après un `useRecordExport` réussi (un export vient d'y ajouter une entrée).
 */
export function useExportJournal(
  campaignId: string,
): UseQueryResult<ExportJournalResponseDto, Error> {
  const client: RankAdminClient = useRankAdminClient();
  return useQuery<ExportJournalResponseDto, Error>({
    queryKey: exportJournalKey(campaignId),
    queryFn: () => client.getExportJournal(campaignId),
  });
}

/**
 * JOURNALISE la métadonnée d'un export benchmark (le fichier reste front, jamais transmis). Au succès,
 * INVALIDE la query du journal pour refléter la nouvelle entrée (append-only, autorité API).
 */
export function useRecordExport(
  campaignId: string,
): UseMutationResult<RecordExportResponseDto, Error, RecordExportBodyDto> {
  const client: RankAdminClient = useRankAdminClient();
  const qc = useQueryClient();
  return useMutation<RecordExportResponseDto, Error, RecordExportBodyDto>({
    mutationFn: (body) => client.recordExport(campaignId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: exportJournalKey(campaignId) });
    },
  });
}
